import { Modal } from 'components/Modal';
import { Title } from 'components/Title';
import { FC, useCallback, useState } from 'react';
import { getAuthorizationHeader } from 'redux/Api/employedsApi/employedsApi';
import { getFileNameFromContentDisposition } from 'utils/strActions';
import styles from './OrdersUploadModal.module.scss';
import { ActionButtons } from './components/ActionButtons';
import { FileInfoDisplay } from './components/FileInfoDisplay';
import { FilePreviewInfo } from './components/FilePreviewInfo';
import { FileUploadArea } from './components/FileUploadArea';
import { ReportDownload } from './components/ReportDownload';
import { StatusInfo } from './components/StatusInfo';
import { TemplateLink } from './components/TemplateLink';
import { UploadSummary } from './components/UploadSummary';
import { AddMultipleOrdersResponse } from 'redux/Api/ordersApi/type';
import { useAddMultipleOrdersMutation } from 'redux/Api/ordersApi/ordersApi';
import useYandexMetrica from 'hooks/useYandexMetrica';

interface OrdersUploadModalTypes {
  setShow: (e: boolean) => void;
  show: boolean;
}

export const OrdersUploadModal: FC<OrdersUploadModalTypes> = ({ setShow, show }) => {
  const { sendGoal } = useYandexMetrica();
  const [uploadResponse, setUploadResponse] = useState<AddMultipleOrdersResponse | null>(
    null,
  );
  const [reportError, setReportError] = useState('');
  const [inviteMultiple, { isLoading }] = useAddMultipleOrdersMutation();

  const [disabled, setDisabled] = useState(false);

  const [file, setFile] = useState<File | null>(null);
  const [step, setStep] = useState<'select' | 'preview' | 'completed'>('select');

  const uploadHandler = async () => {
    sendGoal('CreateOrderValidate');
    if (file) {
      const formData = new FormData();
      formData.append('orders', file);

      await inviteMultiple(formData)
        .then((data: any) => {
          if (data?.data) {
            setUploadResponse(data.data);
            setStep('preview');
          } else if (data?.error) {
            setUploadResponse(null);
            setFile(null);
            setReportError(data?.error.data.errors.orders[0]);
          }
        })
        .catch((e) => {
          console.log('e', e);
          setReportError('Ошибка при загрузке файла.');
        });
    }
  };

  const createOrdersHandler = async () => {
    sendGoal('CreateOrderUpload');
    setDisabled(true);

    if (file) {
      const formData = new FormData();
      formData.append('orders', file);
      formData.append('persist', '1');

      await inviteMultiple(formData)
        .then((data: any) => {
          if (data?.data) {
            setDisabled(false);
            setUploadResponse(data.data);
            setStep('completed');
          } else if (data?.error) {
            setReportError(data?.error.data.errors.orders[0]);
          }
        })
        .catch((e) => {
          console.log('e', e);
        });
    }
  };

  const handleDownloadReport = async () => {
    if (uploadResponse?.report_file) {
      const response = await fetch(uploadResponse.report_file, {
        method: 'GET',
        headers: { Authorization: getAuthorizationHeader() },
      });

      const type = response.headers.get('content-type') || undefined;
      const disposition = response.headers.get('content-disposition');
      const arrayBuffer = await response.blob();

      const blob = new Blob([arrayBuffer], {
        type,
      });
      const url = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', getFileNameFromContentDisposition(disposition));
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    }
  };

  const handleFileSelect = useCallback((selectedFile: File) => {
    setFile(selectedFile);
    setReportError('');
    setStep('select');
    setUploadResponse(null);
  }, []);

  const truncateFileName = (name: string, maxLength = 20) => {
    if (name.length <= maxLength) return name;
    const extension = name.split('.').pop();
    const baseName = name.substring(0, name.lastIndexOf('.'));
    return `${baseName.substring(0, maxLength - extension!.length - 3)}...${extension}`;
  };

  const resetForm = () => {
    setFile(null);
    setUploadResponse(null);
    setStep('select');
    setReportError('');
  };

  const renderCompletedStep = () => (
    <div className={styles.previewInfo}>
      <StatusInfo
        importCount={uploadResponse!.import_count}
        uploadCount={uploadResponse!.upload_count}
        isPreview={false}
      />
      <ReportDownload onDownload={handleDownloadReport} />
      <UploadSummary sum={uploadResponse!.sum} commission={uploadResponse!.commission} />
    </div>
  );

  const renderPreviewStep = () => (
    <>
      <div className={styles.previewInfo}>
        <FilePreviewInfo fileName={file!.name} />
        <StatusInfo
          importCount={uploadResponse!.import_count}
          uploadCount={uploadResponse!.upload_count}
          isPreview={true}
        />
        <ReportDownload onDownload={handleDownloadReport} />
        <UploadSummary
          sum={uploadResponse!.sum}
          commission={uploadResponse!.commission}
        />
      </div>
      <ActionButtons
        onBack={resetForm}
        onPrimaryAction={createOrdersHandler}
        isLoading={isLoading || disabled}
        primaryButtonText={isLoading || disabled ? 'Создаем заказы' : 'Создать заказы'}
        primaryActionIcon='check'
      />
    </>
  );

  const renderSelectStep = () => (
    <div className={styles.wrapper}>
      <span className={styles.subTitle}>
        Подготовь файл в формате XLSX с реестром заданий. Используй приложенный шаблон,
        чтобы реестр обработался корректно.
      </span>
      <TemplateLink hasFile={!!file} />
      {file ? (
        <>
          <FileInfoDisplay
            file={file}
            onRemove={() => setFile(null)}
            truncateFileName={truncateFileName}
          />
          <ActionButtons
            onBack={resetForm}
            onPrimaryAction={uploadHandler}
            isLoading={isLoading}
            primaryButtonText={isLoading ? 'Проверяем' : 'Проверить'}
            primaryActionIcon='done'
          />
        </>
      ) : (
        <FileUploadArea onFileSelect={handleFileSelect} error={reportError} />
      )}
    </div>
  );

  return (
    <Modal setOpen={setShow} open={show}>
      <div className={styles.root}>
        <div>
          <Title title='Загрузка реестра заданий' />
        </div>

        {step === 'completed' && uploadResponse && renderCompletedStep()}
        {step === 'preview' && uploadResponse && renderPreviewStep()}
        {step === 'select' && renderSelectStep()}
      </div>
    </Modal>
  );
};
